import { useEffect, type CSSProperties, type ReactNode } from "react";
import { Ico } from "../lib/icons";
import { BotAvatar, BottomNav, MainButton, type BottomNavKey, type MainButtonProps } from "./atoms";

// Narrow inline casts: the global TelegramWebApp type covers the
// methods we actually invoke today (setText/setParams/onClick/offClick
// on MainButton; show/hide/onClick/offClick on BackButton), but some
// older TG clients are missing showProgress/hideProgress, and we want
// to treat every method as optional at the call site so we can wrap
// invocations in try/catch defensively.
type TgMainButton =
  | (NonNullable<TelegramWebApp["MainButton"]> & {
      setParams?: (p: {
        is_active?: boolean;
        is_visible?: boolean;
        text?: string;
        color?: string;
        text_color?: string;
      }) => void;
      showProgress?: (leaveActive?: boolean) => void;
      hideProgress?: () => void;
      setText?: (t: string) => void;
      onClick?: (cb: () => void) => void;
      offClick?: (cb: () => void) => void;
      show?: () => void;
      hide?: () => void;
    })
  | undefined;

type TgBackButton =
  | (NonNullable<TelegramWebApp["BackButton"]> & {
      onClick?: (cb: () => void) => void;
      offClick?: (cb: () => void) => void;
      show?: () => void;
      hide?: () => void;
    })
  | undefined;

// We only render our own header bar when running outside the Telegram
// WebView. Inside TG, the native title strip already shows the bot name
// and a Close affordance — duplicating both is the most-complained-about
// "this looks like a webview" tell. initData is the cleanest signal: the
// telegram-web-app.js script always defines window.Telegram.WebApp, but
// the initData string is empty whenever the page isn't being hosted by
// an actual Telegram client (browser preview, manual URL load, etc).
const isInsideTelegram = (): boolean => {
  if (typeof window === "undefined") return false;
  const wa = window.Telegram?.WebApp;
  return typeof wa?.initData === "string" && wa.initData.length > 0;
};

const iconBtn: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: 22,
};

export function TgScreen({
  children,
  title,
  onBack,
  onClose,
  menu = true,
  mainButton,
  bottomNav,
  bottomNavHint,
  onTabSelect,
  noPad = false,
  sheetOpen,
}: {
  children?: ReactNode;
  title?: string;
  onBack?: () => void;
  onClose?: () => void;
  menu?: boolean;
  mainButton?: MainButtonProps;
  bottomNav?: BottomNavKey;
  /** Optional onboarding hint — pulses a dot on the named tab. */
  bottomNavHint?: BottomNavKey;
  onTabSelect?: (k: BottomNavKey) => void;
  noPad?: boolean;
  sheetOpen?: ReactNode;
}) {
  const inTg = isInsideTelegram();
  // When the native MainButton is bound (inside TG, mainButton set, no
  // bottom-nav), suppress the HTML MainButton render below — the native
  // one lives in TG chrome outside the WebView and would otherwise be
  // duplicated.
  const useNativeMainButton = inTg && !!mainButton && !bottomNav;
  // Same idea for the inline "← Back" row: when we bind TG's native
  // BackButton, the chrome already shows the affordance.
  const useNativeBackButton = inTg && !!onBack;

  // Bind TG's native BackButton when we have an onBack handler and
  // we're inside Telegram. Older clients may be missing one of the
  // methods, so each call is wrapped defensively.
  useEffect(() => {
    if (!inTg || !onBack) return;
    const bb = window.Telegram?.WebApp?.BackButton as TgBackButton;
    if (!bb) return;
    try {
      bb.show?.();
    } catch {
      /* noop */
    }
    try {
      bb.onClick?.(onBack);
    } catch {
      /* noop */
    }
    return () => {
      try {
        bb.offClick?.(onBack);
      } catch {
        /* noop */
      }
      try {
        bb.hide?.();
      } catch {
        /* noop */
      }
    };
  }, [inTg, onBack]);

  // Bind TG's native MainButton when inside TG, mainButton is set, and
  // no bottom-nav is shown (otherwise the two would visually compete —
  // the native MainButton sits in the TG chrome strip below the WebView
  // and would overlap the bottom-nav row).
  useEffect(() => {
    if (!inTg || !mainButton || bottomNav) return;
    const mb = window.Telegram?.WebApp?.MainButton as TgMainButton;
    if (!mb) return;
    const cb = mainButton.onClick;
    try {
      mb.setText?.(mainButton.text ?? "Continue");
    } catch {
      /* noop */
    }
    try {
      const params: {
        is_active?: boolean;
        is_visible?: boolean;
        color?: string;
        text_color?: string;
      } = {
        is_active: !mainButton.disabled,
        is_visible: true,
      };
      // Only forward explicit color overrides — leaving these unset
      // preserves the user's TG theme defaults.
      if (mainButton.color) params.color = mainButton.color;
      mb.setParams?.(params);
    } catch {
      /* noop */
    }
    try {
      if (mainButton.loading) mb.showProgress?.();
      else mb.hideProgress?.();
    } catch {
      /* noop */
    }
    // Belt-and-suspenders: setParams({ is_visible: true }) handles
    // visibility on modern clients, but show() is the canonical call.
    try {
      mb.show?.();
    } catch {
      /* noop */
    }
    if (cb) {
      try {
        mb.onClick?.(cb);
      } catch {
        /* noop */
      }
    }
    return () => {
      if (cb) {
        try {
          mb.offClick?.(cb);
        } catch {
          /* noop */
        }
      }
      try {
        mb.hideProgress?.();
      } catch {
        /* noop */
      }
      try {
        mb.hide?.();
      } catch {
        /* noop */
      }
    };
  }, [
    inTg,
    bottomNav,
    mainButton,
    mainButton?.text,
    mainButton?.disabled,
    mainButton?.loading,
    mainButton?.color,
    mainButton?.onClick,
  ]);

  return (
    <div
      className="tg-app"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Fake chrome — only when NOT inside Telegram. Provides a header
          for the browser preview / standalone modes. Inside TG, the
          native title bar handles all of this. */}
      {!inTg && (
        <div
          style={{
            flexShrink: 0,
            height: 44,
            marginTop: 54,
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            background: "var(--tg-chrome, var(--bg))",
            borderBottom: "0.5px solid var(--border)",
            position: "relative",
            zIndex: 5,
            fontFamily: "var(--font-text)",
          }}
        >
          <div style={{ minWidth: 72, display: "flex", alignItems: "center" }}>
            {onBack ? (
              <button
                onClick={onBack}
                style={{
                  ...iconBtn,
                  width: "auto",
                  height: 30,
                  padding: "0 4px",
                  gap: 2,
                  color: "var(--link)",
                }}
              >
                <Ico.back style={{ color: "var(--link)" }} />
                <span style={{ color: "var(--link)", fontSize: 17, fontWeight: 400 }}>Back</span>
              </button>
            ) : (
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  color: "var(--link)",
                  fontSize: 17,
                  fontWeight: 400,
                  fontFamily: "var(--font-text)",
                }}
              >
                Close
              </button>
            )}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <BotAvatar size={26} />
            <div style={{ minWidth: 0, textAlign: "left" }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--text)",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                Shieldz
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                mini app
              </div>
            </div>
          </div>

          <div style={{ minWidth: 72, display: "flex", justifyContent: "flex-end" }}>
            {menu && (
              <button style={{ ...iconBtn, width: 30, height: 30 }} aria-label="menu">
                <Ico.menu style={{ color: "var(--hint)" }} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Inside TG, the native back button is the X in the corner —
          but if the screen needs an explicit "Back to wallet" affordance
          (e.g. from Send/Swap), render a slim text-only button row that
          doesn't compete with TG's chrome. */}
      {/* Inline back row only renders when we're in TG, have an onBack,
          AND for some reason the native BackButton wasn't bound (e.g. a
          future flag). Today useNativeBackButton mirrors `inTg && onBack`
          exactly, so this branch is effectively gated to standalone — but
          we keep the structure so that suppressing the native binding
          would automatically re-show the inline affordance. */}
      {inTg && onBack && !useNativeBackButton && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 12px 0",
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              padding: "6px 4px",
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
              color: "var(--link)",
              fontSize: 15,
              fontWeight: 500,
              fontFamily: "var(--font-text)",
            }}
          >
            <Ico.back style={{ color: "var(--link)" }} />
            <span>Back</span>
          </button>
        </div>
      )}

      {/* scrollable content */}
      <div
        className="tg-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: noPad ? 0 : "0 0 12px",
          position: "relative",
        }}
      >
        {title && (
          <div
            style={{
              padding: "12px 16px 4px",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text)",
            }}
          >
            {title}
          </div>
        )}
        {children}
      </div>

      {/* Main Button + Bottom Nav stack — always pinned to the bottom.
          flexShrink: 0 keeps it out of the scroll region; the explicit
          safe-area padding prevents the iOS home indicator from
          overlapping the tab labels.

          When we've bound the native TG MainButton (useNativeMainButton),
          the HTML one is suppressed — but we still render the wrapper
          if bottomNav is set, because BottomNav lives in the same strip.
          The conditional below skips the whole block when there's
          nothing left to draw. */}
      {((mainButton && !useNativeMainButton) || bottomNav) && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            paddingBottom: bottomNav
              ? "max(8px, env(safe-area-inset-bottom))"
              : "max(34px, env(safe-area-inset-bottom))",
            background: "var(--bg)",
            borderTop: bottomNav ? "0.5px solid var(--border)" : "none",
            position: "relative",
            zIndex: 6,
          }}
        >
          {mainButton && !useNativeMainButton && <MainButton {...mainButton} />}
          {bottomNav && (
            <BottomNav
              active={bottomNav}
              onSelect={onTabSelect}
              hint={bottomNavHint}
            />
          )}
        </div>
      )}

      {sheetOpen}
    </div>
  );
}
